import React, { useContext, useEffect, useState } from "react";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import Avatar from "@mui/material/Avatar";
import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";
import default_background from "assets/images/upstyled/default_background.png";
import { capitalCase } from "capital-case";
import SuiBadge from "components/SuiBadge";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import FollowButton from "pages/social/followbutton";
import { useNavigate } from "react-router-dom";
import { MANUFACTURER_SERVICES, accountTypeOptions } from "utils/data";
import { formatAddress } from "utils/helper";
import { matchYoutubeUrl } from "utils/imageHelper";
import { AccountContext } from "context/account";

const ProfileCard = ({ profile = {}, sx = {} }) => {
  const navigate = useNavigate();
  const account = useContext(AccountContext);

  const store = profile;
  const serviceStages = store?.metadata?.services_offered
    ? Object.keys(store.metadata.services_offered)
    : [];
  let image = "";
  const gallery = store?.gallery || [];
  for (let i = 0; i < gallery.length; i++) {
    if (
      gallery[i].startsWith("blob") ||
      matchYoutubeUrl(gallery[i]) ||
      gallery[i].endsWith(".mp4")
    ) {
    } else {
      image = gallery[i];
      break;
    }
  }

  if (!image) {
    //trying to use background image
    image = store?.metadata?.background_image;
  }

  if (!image) {
    //using default image
    image = default_background;
  }

  const address = formatAddress(store?.metadata?.address, true);
  const services = serviceStages;
  const renderServiceBadge = MANUFACTURER_SERVICES.filter((s) => services.includes(s.value)).map(
    (s, index) => {
      return (
        <div>
          <SuiBox key={index} mr={index === s.length - 1 ? 0 : 0.5} mb={0.5}>
            <SuiBadge
              textTransform="none"
              color={s.color}
              size="xs"
              badgeContent={capitalCase(s.label)}
              container
            />
          </SuiBox>
        </div>
      );
    }
  );

  const accountType = profile?.metadata?.account_role || profile.account_role;
  const accountRole = accountTypeOptions.find((x) => x.value == accountType);

  return (
    <Card
      sx={{
        maxWidth: 360,
        height: "100%",
        boxShadow: "none",
        overflow: "hidden",
        borderColor: ({ borders }) => borders.borderColor,
        boxShadow: ({ boxShadows: { md } }) => md,
        borderRadius: "xl",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <SuiBox>
        <SuiBox
          position="relative"
          height={160}
          border="none"
          sx={{
            backgroundImage: image
              ? `url(${image})`
              : ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                  `${linearGradient(
                    rgba(gradients.secondary.main, 0.2),
                    rgba(gradients.secondary.state, 0.4)
                  )}`,
            backgroundSize: "cover",
            backgroundPosition: "50%",
            overflow: "hidden",
            cursor: "pointer",
          }}
          onClick={() =>
            account?.id
              ? navigate(`/wall/portfolio/${profile.id}`)
              : navigate(`/public/portfolio/${profile.id}`)
          }
        />
      </SuiBox>
      <SuiBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        sx={{ mt: -3.5, px: 1.5 }}
      >
        <SuiBox pb={0.5}>
          <Avatar
            src={store?.metadata?.avatar}
            sx={{ width: 46, height: 46, cursor: "pointer" }}
            onClick={() =>
              account?.id
                ? navigate(`/wall/portfolio/${profile.id}`)
                : navigate(`/public/portfolio/${profile.id}`)
            }
          />
        </SuiBox>
        <SuiBadge
          badgeContent={
            <SuiTypography
              variant="body2"
              fontWeight="bold"
              color="white"
              sx={{ lineHeight: 1, px: 0.5, py: 0.2 }}
            >
              {accountRole ? accountRole.label : "Designer"}
            </SuiTypography>
          }
          size="xxs"
          varinat="contained"
        />
      </SuiBox>
      <SuiBox px={1.5} sx={{ minHeight: "130px" }}>
        <SuiBox>
          <SuiBox display="flex" alignItems="flex-start" justifyContent="space-between">
            <SuiBox display="flex" alignItems="center" gap={0.5}>
              <Tooltip title={store?.name}>
                <SuiTypography
                  variant="h6"
                  fontWeight="medium"
                  textTransform="capitalize"
                  lineHeight="1.2"
                  sx={{
                    maxWidth: 360,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    account?.id
                      ? navigate(`/wall/portfolio/${profile.id}`)
                      : navigate(`/public/portfolio/${profile.id}`)
                  }
                >
                  {store?.name}
                </SuiTypography>
              </Tooltip>
            </SuiBox>
          </SuiBox>
          {address ? (
            <SuiBox display="flex" my={0.5} justifyContent="space-between" alignItems="center">
              <SuiTypography
                variant="body2"
                fontWeight="regular"
                color="secondary"
                display="flex"
                alignItems="center"
                lineHeight={1}
              >
                <LocationOnIcon />
                {address}
              </SuiTypography>
            </SuiBox>
          ) : (
            <br />
          )}
        </SuiBox>
        <SuiBox mb={1} lineHeight={0}>
          {store?.detail ? (
            <Tooltip title={store?.detail}>
              <SuiTypography
                variant="button"
                fontWeight="regular"
                color="text"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "2",
                  WebkitBoxOrient: "vertical",
                  minHeight: "28px",
                  lineHeight: 1,
                }}
              >
                {store?.detail}
              </SuiTypography>
            </Tooltip>
          ) : (
            <SuiTypography
              variant="button"
              fontWeight="regular"
              color="text"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
                minHeight: "28px",
                lineHeight: 1,
              }}
            >
              {store?.metadata?.story
                ? store?.metadata?.story.slice(0, 500)
                : "There is no description for this user!"}
            </SuiTypography>
          )}
          <SuiBox display="flex" alignItems="center" sx={{ flexWrap: "wrap", mt: 1 }}>
            {renderServiceBadge}
          </SuiBox>
        </SuiBox>
      </SuiBox>
      <SuiBox sx={{ flex: 1 }} />
      <SuiBox px={1.5} pb={1} sx={{ alignSelf: "flex-end", flex: "none", width: "100%" }}>
        <SuiTypography lineHeight={1} variant="body2" fontWeight="bold">
          {profile.mutualcounts} Mutual Followers
        </SuiTypography>
        <FollowButton user={profile} size={"small"} />
      </SuiBox>
    </Card>
  );
};

export default ProfileCard;
